import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { NgClass } from '@angular/common';
import {
  BadgeComponent,
  KeeniconComponent,
  LoaderComponent,
} from '../../partials';
import {
  CustomTooltipDirective,
  DebounceInputDirective,
  NoLeadingSpaceDirective,
} from '@core-utils';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { AutocompleteItemInterface } from '../../types';
import { TranslocoDirective } from '@jsverse/transloco';
import { uiKitScope } from '../../constants/ui-constant';
import { provideUiKitScope } from '../../providers';

@Component({
  selector: 'ui-form-autocomplete',
  imports: [
    NgClass,
    BadgeComponent,
    CustomTooltipDirective,
    DebounceInputDirective,
    KeeniconComponent,
    LoaderComponent,
    NgbTooltip,
    TranslocoDirective,
    NoLeadingSpaceDirective,
  ],
  templateUrl: './form-autocomplete.component.html',
  styleUrl: './form-autocomplete.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideUiKitScope()],
})
export class FormAutocompleteComponent {
  private elementRef = inject(ElementRef);

  placeholder = input<string>('');
  value = input<string>('');
  items = input<AutocompleteItemInterface[]>([]);
  isLoading = input<boolean>(false);
  debounceTime = input<number>(500);
  global = input<boolean>(true);
  multiple = input<boolean>(false);
  showSelectedBadges = input<boolean>(false);
  minSearchLength = input<number>(0);
  description = input<string>('');
  noResultsMessage = input<string>('');

  debounceInput = output<Event>();
  itemSelected = output<AutocompleteItemInterface>();
  itemsSelected = output<AutocompleteItemInterface[]>();

  protected readonly uiKitScope = uiKitScope;
  isDropdownOpen = signal<boolean>(false);
  selectedItems = signal<AutocompleteItemInterface[]>([]);
  inputValue = signal<string>('');

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const clickedInside = this.elementRef.nativeElement.contains(
      event.target as Node,
    );
    if (!clickedInside && this.isDropdownOpen()) {
      this.closeDropdown();
    }
  }

  onInput(event: Event) {
    const input = event.target as HTMLInputElement;
    this.inputValue.set(input.value);

    if (input.value.length >= this.minSearchLength()) {
      this.isDropdownOpen.set(true);
      this.debounceInput.emit(event);
    } else {
      this.isDropdownOpen.set(false);
    }
  }

  onFocus() {
    const hasValue = this.multiple() ? this.value() : this.inputValue();
    if (hasValue && (this.items().length > 0 || this.isLoading())) {
      this.isDropdownOpen.set(true);
    }
  }

  selectItem(item: AutocompleteItemInterface) {
    if (item.disabled) return;

    if (this.multiple()) {
      const currentSelected = this.selectedItems();
      const index = currentSelected.findIndex((i) => i.id === item.id);

      if (index === -1) {
        const newSelected = [...currentSelected, item];
        this.selectedItems.set(newSelected);
        this.itemsSelected.emit(newSelected);
      } else {
        const newSelected = currentSelected.filter((i) => i.id !== item.id);
        this.selectedItems.set(newSelected);
        this.itemsSelected.emit(newSelected);
      }
    } else {
      this.inputValue.set(item.name);
      this.itemSelected.emit(item);
      this.closeDropdown();
    }
  }

  removeSelectedItem(item: AutocompleteItemInterface, event: Event) {
    event.stopPropagation();
    const newSelected = this.selectedItems().filter((i) => i.id !== item.id);
    this.selectedItems.set(newSelected);
    this.itemsSelected.emit(newSelected);
  }

  removeSelectedItemById(itemId: string) {
    const newSelected = this.selectedItems().filter((i) => i.id !== itemId);
    this.selectedItems.set(newSelected);
    this.itemsSelected.emit(newSelected);
  }

  isItemSelected(item: AutocompleteItemInterface): boolean {
    return this.selectedItems().some((i) => i.id === item.id);
  }

  closeDropdown() {
    this.isDropdownOpen.set(false);
  }
}
